import React, { useEffect, memo, useState, useCallback } from 'react';
import './index.less';
import ExamSyllabus from './components/exam-syllabus';
import CompletedAnimation from './components/completed-animation';
import PredictionScoreCard from './components/prediction-score-card';
import UserReportCard from './components/user-report-card';
import NoticeBar from './components/notice-bar';
export { default as AppProvider } from './context/AppProvider';
import { useAuthContext } from './context/AuthContext';
import { useCommonInfoContext } from './context/CommonInfoProvider';
import { IHomePagePredictionScore, IHomePageExamSyllabus, IPredictScoreCompletedExamPoint } from '@/types';
import { getHomePagePredictionScore, getPredictScoreCompletedExamPoint } from '@/apis';
import globalStore from '@/store/global';
import { environment } from '@gaodun.com/gtools';
import classNames from 'classnames';

const Index = () => {
    const [initInfo, setInitInfo] = useState<{
        projectId: number;
        subjectId: number;
        examSeasonId: number;
        isLoggedIn: boolean;
        sourceFromType: number;
        businessParam?: { [key: string]: number }; // 课程场景下可能会传
    }>();
    const [examSyllabus, setExamSyllabus] = useState<IHomePageExamSyllabus>({
        contents: [],
        examSyllabusId: 0,
        title: '',
        projectId: 0,
        subjectId: 0,
        knowledgeCount: 0,
    });
    const [predictionScore, setPredictionScore] = useState<IHomePagePredictionScore['predictionScore']>({
        goalScore: 0,
        predictionScore: 0,
        examSeasonId: 0,
        examSyllabusId: 0,
        totalScore: 0,
        studentPredictionStatus: 0,
        predictionScoreIntroduceH5Url: '',
    });
    const [userReportCalendar, setUserReportCalendar] = useState<{
        userReportUrl: string;
        userReportDay: string[];
    }>();
    const [completedExamPoint, setCompletedExamPoint] = useState<{
        count: number;
        list: IPredictScoreCompletedExamPoint['knowledgeList'][];
    }>();
    const [notice, setNotice] = useState<{
        title: string;
        content: string;
        hasNotice: boolean;
    }>({
        title: '',
        content: '',
        hasNotice: false,
    });

    const { authActions } = useAuthContext();
    const { dispatch: commonInfoDispatch } = useCommonInfoContext();

    const fetchData = useCallback(async () => {
        const { projectId, subjectId, examSeasonId, isLoggedIn } = initInfo!;
        const response = await getHomePagePredictionScore(
            {
                projectId,
                subjectId,
                examSeasonId,
            },
            { isVisit: !initInfo!.isLoggedIn }
        );
        if (response.status === 0 && response.result) {
            const { examSyllabus, hasAuth, noAuthTip, predictionScore, userReportCalendar, noticeContent, noticeTitle, noticesStatus } =
                response.result;
            const { studentPredictionStatus, predictionScoreIntroduceH5Url } = predictionScore || {};

            commonInfoDispatch({
                type: 'UPDATE_COMMON_INFO',
                ...initInfo!,
                examSyllabusId: examSyllabus.examSyllabusId,
            });

            authActions.setAuthStatus({
                isLoggedIn: isLoggedIn,
                hasAuth: hasAuth === 1,
                noAuthTip: noAuthTip,
                hasPredictionScore: studentPredictionStatus !== 0,
                predictionScoreUrl: predictionScoreIntroduceH5Url,
            });

            setExamSyllabus(examSyllabus);
            setPredictionScore(predictionScore);
            setNotice({
                title: noticeTitle,
                content: noticeContent,
                hasNotice: noticesStatus === 1,
            });
            setUserReportCalendar({
                userReportUrl: userReportCalendar?.userReportUrl ?? '',
                userReportDay:
                    userReportCalendar?.practiceStatusOfWeek
                        .map((status, index) => (status === 1 ? `${index}` : undefined))
                        .filter(day => day !== undefined) ?? [],
            });
        }
    }, [initInfo, authActions, commonInfoDispatch]);

    const fetchCompletedExamPoint = useCallback(async () => {
        const { projectId, subjectId, examSeasonId, isLoggedIn } = initInfo!;

        if (!isLoggedIn) return;

        const response = await getPredictScoreCompletedExamPoint({
            projectId,
            subjectId,
            examSeasonId,
            devMode: environment.getApiEnv() === 't-' && localStorage.getItem('devMode') ? 1 : 0,
        });

        if (response.status === 0 && response.result?.count) {
            const { knowledgeList, count } = response.result;
            const list = [];
            for (let i = 0; i < knowledgeList.length; i += 3) {
                list.push(knowledgeList.slice(i, i + 3));
            }
            setCompletedExamPoint({ count, list });
        }
    }, [initInfo]);

    useEffect(() => {
        const initialize = async () => {
            if (initInfo?.examSeasonId) {
                await fetchData();
                await fetchCompletedExamPoint();
            }
        };

        initialize();
    }, [initInfo?.examSeasonId]);

    useEffect(() => {
        globalStore.subscribe('PredictionScore', ({ type, params }) => {
            switch (type) {
                case 'init':
                    setInitInfo({
                        ...params,
                        isLoggedIn: params.isLogin ?? true, // 课程内默认登录状态
                        sourceFromType: params.sourceFromType ?? 100534177, // 100534177 新题库id
                    });

                    authActions.updateLoginStatus(params.isLogin ?? true);
                    break;
                case 'open_predict_score_feedback':
                    globalStore.publish('PredictScoreFeedback', { type: 'open', params });
                    break;
                default:
                    setInitInfo(undefined);
                    break;
            }
        });

        return () => {
            globalStore.unsubscribe('PredictionScore');
        };
    }, [authActions]);

    return (
        <>
            <div
                className={classNames('sub-tiku__predict-score-container', {
                    'sub-tiku__predict-score_has-notice': notice.hasNotice,
                })}
            >
                <div className="sub-tiku__predict-score-flex-center">
                    <PredictionScoreCard dataSource={predictionScore} />
                    <div className="sub-tiku__predict-score-placeholder-gap" />
                    <UserReportCard userReportCalendar={userReportCalendar} />
                </div>
                {notice.hasNotice && <NoticeBar content={notice.content} title={notice.title} />}
            </div>
            <ExamSyllabus examSyllabus={examSyllabus} />
            {completedExamPoint?.count && (
                <CompletedAnimation dataSource={completedExamPoint} onClose={() => setCompletedExamPoint(undefined)} />
            )}
        </>
    );
};

export default memo(Index);
